/**
 * Shared types for the heb/zijn drill app.
 * Mirrors SPEC §4 (exercise schema) and §8 (storage schema).
 */

export type Level = 1 | 2 | 3 | 4
export type Subject =
  | 'ik' | 'jij' | 'hij' | 'zij-sg' | 'het'
  | 'wij' | 'jullie' | 'zij-pl'
  | 'np-sg' | 'np-pl'

export type Auxiliary = 'hebben' | 'zijn'

export type RuleKey =
  | 'beweging-richting'
  | 'beweging-duur'
  | 'zijn-blijven'
  | 'verandering'
  | 'vaste-zijn'
  | 'transitief'
  | 'default-hebben'
  | 'reflexief'
  | 'groeps-inf'
  | 'passief'

export interface Exercise {
  id: string            // "l1-001" … "l4-500"
  level: Level
  text: string          // contains exactly one "___"
  answer: Auxiliary     // lemma
  forms: { hebben: string; zijn: string }  // both conjugated for this subject
  subject: Subject
  participle: string    // main verb group as it appears
  rule: RuleKey
  /** Russian translation of the full sentence (gap = verb to fill).
   *  Must contain exactly one `___` placeholder at the same position. */
  translationRu: string
  note?: string | null  // present only for adjudicated/ambiguous items
}

export interface LevelMeta {
  level: Level
  name: string   // "Niveau 1"
  cefr: string   // "A1–A2"
  descRu: string // "Короткие простые фразы"
}

export interface ExercisesFile {
  version: 1
  levels: LevelMeta[]
  exercises: Exercise[]
}

// ── Storage schema (SPEC §8) ────────────────────────────────────────────
export interface StorageState {
  v: 1
  mastered: Record<string, 1>     // O(1) lookup; only present-or-not matters
  wrong: Record<string, number>   // lifetime wrong count (kept after mastering)
  stats: {
    firstUse: string              // YYYY-MM-DD
    streak: { count: number; lastDay: string }  // local-date strings
  }
  settings: { sessionSize: 10 | 25 | 50 }
}

export const STORAGE_KEY = 'hoz.v1'
export const DEFAULT_SETTINGS: StorageState['settings'] = { sessionSize: 10 }
